"use client";
import React, { useState, useRef } from 'react';
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from 'framer-motion';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Image from 'next/image';
import { useContactModal } from '../contexts/ContactModalContext';
import { Phone } from 'lucide-react';
import { PHONE_DISPLAY, PHONE_TEL, whatsappHref, DEFAULT_WHATSAPP_MESSAGE } from '@/lib/contact';

const dropdownServices = [
    {
        tag: "UI / UX Design",
        title: "UI/UX Design & Digital Experiences",
        image: "/images/services/ux-design/hero.webp",
        href: "/services/ux-design",
        objectPosition: "center center",
    },
    {
        tag: "Web Development",
        title: "Digital Product Development",
        image: "/images/services/website-design/hero.webp",
        href: "/services/web-development",
        objectPosition: "center center",
    },
    {
        tag: "Search & Visibility",
        title: "SEO: Be Found Everywhere Your Customers Search",
        image: "/images/services/lso/hero.webp",
        href: "/services/search-visibility",
        objectPosition: "left center",
    },
    {
        tag: "Insight-Driven Marketing",
        title: "Performance Marketing",
        image: "/images/services/performance-marketing/nav_performance.webp",
        href: "/services/performance-marketing",
        objectPosition: "center center",
    },
    {
        tag: "Social Media Marketing",
        title: "Skyrocket your Social Media Presence",
        image: "/images/services/social-media/hero.webp",
        href: "/services/social-media",
        objectPosition: "center center",
    },
    {
        tag: "Branding & Creative Strategy",
        title: "Visual Communication & Branding",
        image: "/images/services/brand-strategy/hero.webp",
        href: "/services/brand-strategy",
        objectPosition: "center center",
    },
];

const dropdownOurWork = [
    { label: 'Web Design & Development', href: '/our-work/web-design' },
    { label: 'Social Media Marketing', href: '/our-work/social-media-marketing' },
    { label: 'Meta Ads & Performance Marketing', href: '/our-work/meta-ads' },
    { label: 'Case Studies', href: '/our-work/case-studies' },
];

const navItems = [
    { name: 'Home', href: '/' },
    { name: 'About Us', href: '/about' },
    { name: 'Services', href: '/services', hasDropdown: true },
    { name: 'Industries', href: '/industries' },
    { name: 'Our Work', href: '/our-work', hasOurWorkDropdown: true },
    { name: 'Blogs', href: '/blogs' },
];

export default function Navbar() {
    const pathname = usePathname();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const { openModal } = useContactModal();
    const [hidden, setHidden] = useState(false);
    const [servicesOpen, setServicesOpen] = useState(false);
    const [hoveredSvc, setHoveredSvc] = useState(0);
    const [ourWorkOpen, setOurWorkOpen] = useState(false);
    const ourWorkTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const isLightPage = (pathname.startsWith('/services') && pathname !== '/services/performance-marketing' && pathname !== '/services/ux-design' && pathname !== '/services/web-development' && pathname !== '/services/social-media' && pathname !== '/services/performance-marketing/google-ads' && pathname !== '/services/performance-marketing/meta-ads' && pathname !== '/services/performance-marketing/marketing-analytics') || pathname.startsWith('/industries') || pathname.startsWith('/contact') || pathname.startsWith('/blogs') || pathname.startsWith('/privacy-policy') || pathname.startsWith('/web-development-company-in-bangalore');

    const { scrollY } = useScroll();

    useMotionValueEvent(scrollY, "change", (latest) => {
        const previous = scrollY.getPrevious() ?? 0;
        if (latest > previous && latest > 150 && !mobileMenuOpen) {
            setHidden(true);
        } else {
            setHidden(false);
        }
    });

    const openDropdown = () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        setServicesOpen(true);
    };

    const closeDropdown = () => {
        closeTimer.current = setTimeout(() => setServicesOpen(false), 120);
    };

    return (
        <motion.nav
            variants={{ visible: { y: 0 }, hidden: { y: "-110%" } }}
            animate={hidden ? "hidden" : "visible"}
            transition={{ duration: 0.35, ease: "easeInOut" }}
            className="fixed w-full z-50 py-6"
        >
            <div className="max-w-[100rem] mx-auto pl-4 pr-4 md:pl-0 md:pr-8">
                <div className="flex justify-between items-center relative">

                    {/* Brand Logo */}
                    <Link href="/" className="group pointer-events-auto z-10 shrink-0 relative md:-ml-6 -mt-2 md:-mt-3">
                        <div className={`absolute -left-2 -top-1 w-2 h-2 border-l border-t transition-colors duration-500 ${isLightPage ? 'border-blue-600' : 'border-blue-500/50'}`} />
                        {/* Mobile logo: icon + text side by side, no overlap */}
                        <motion.div className="flex md:hidden items-center gap-1.5" initial="initial" whileHover="hover">
                            <Image
                                src="/images/shared/logo_favi_color.webp"
                                alt="Lead Workz Icon"
                                width={160}
                                height={122}
                                className="h-10 w-auto object-contain"
                                style={isLightPage ? {} : { filter: 'brightness(0) invert(1)' }}
                                quality={70}
                                priority
                            />
                            <div className="flex flex-col gap-0">
                                <span className={`text-[16px] font-bold tracking-widest uppercase leading-tight ${isLightPage ? 'text-gray-900' : 'text-white'}`}>
                                    Lead Workz
                                </span>
                                <p className="text-[7px] tracking-[0.12em] uppercase font-light" style={{ color: isLightPage ? '#3730a3' : '#CEFF1A' }}>
                                    Build for visibility.
                                </p>
                            </div>
                        </motion.div>
                        {/* Desktop logo: overlapping design */}
                        <motion.div className="hidden md:flex items-center -space-x-16 pl-1" initial="initial" whileHover="hover">
                            <Image
                                src="/images/shared/logo_favi_color.webp"
                                alt="Lead Workz Icon"
                                width={280}
                                height={214}
                                className="h-28 w-auto object-contain"
                                style={isLightPage ? {} : { filter: 'brightness(0) invert(1)' }}
                                quality={70}
                                priority
                            />
                            <div className="flex flex-col gap-0.5">
                                <span className={`text-[22px] font-bold tracking-widest uppercase ${isLightPage ? 'text-gray-900' : 'text-white'}`}>
                                    Lead Workz
                                </span>
                                <p className="text-[6px] tracking-widest uppercase font-light pl-0.5 transition-all duration-300 group-hover:text-[7px] group-hover:tracking-[0.2em]" style={{ color: isLightPage ? '#3730a3' : '#CEFF1A' }}>
                                    Build for visibility. Optimized for revenue.
                                </p>
                            </div>
                        </motion.div>
                    </Link>

                    {/* Desktop Nav Pill */}
                    <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 hidden lg:block">
                        <div className="relative group">
                            <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-full blur opacity-0 group-hover:opacity-100 transition duration-1000" />

                            <div className="relative flex items-center gap-0 p-1 bg-black/80 backdrop-blur-3xl border border-white/10 rounded-full shadow-[0_0_30px_rgba(0,0,0,0.5)] overflow-visible whitespace-nowrap">
                                {navItems.map((item) =>
                                    item.hasOurWorkDropdown ? (
                                        <div
                                            key={item.name}
                                            className="relative"
                                            onMouseEnter={() => { if (ourWorkTimer.current) clearTimeout(ourWorkTimer.current); setOurWorkOpen(true); }}
                                            onMouseLeave={() => { ourWorkTimer.current = setTimeout(() => setOurWorkOpen(false), 120); }}
                                        >
                                            <Link
                                                href={item.href}
                                                onClick={() => setOurWorkOpen(false)}
                                                className={`text-[11px] uppercase font-bold tracking-[0.08em] transition-all px-4 py-2.5 rounded-full relative flex items-center gap-1.5 whitespace-nowrap ${pathname.startsWith('/our-work')
                                                    ? 'text-white'
                                                    : 'text-white/50 hover:text-white'
                                                    }`}
                                            >
                                                {pathname.startsWith('/our-work') && (
                                                    <motion.div
                                                        layoutId="pill-background-futuristic"
                                                        className="absolute inset-0 bg-blue-600 rounded-full"
                                                        initial={false}
                                                        transition={{ type: "spring", stiffness: 500, damping: 35 }}
                                                    />
                                                )}
                                                <span className="relative z-10">{item.name}</span>
                                                <motion.svg
                                                    animate={{ rotate: ourWorkOpen ? 180 : 0 }}
                                                    transition={{ duration: 0.2 }}
                                                    className="relative z-10 w-3 h-3 opacity-60"
                                                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                                </motion.svg>
                                            </Link>

                                            <AnimatePresence>
                                                {ourWorkOpen && (
                                                    <>
                                                        <motion.div
                                                            initial={{ opacity: 0 }}
                                                            animate={{ opacity: 1 }}
                                                            exit={{ opacity: 0 }}
                                                            className="absolute top-[calc(100%+4px)] left-1/2 -translate-x-1/2 z-[51]"
                                                            style={{ width: 0, height: 0, borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderBottom: '8px solid #0d0f1a' }}
                                                        />
                                                        <motion.div
                                                            initial={{ opacity: 0, y: 10, scale: 0.96 }}
                                                            animate={{ opacity: 1, y: 0, scale: 1 }}
                                                            exit={{ opacity: 0, y: 10, scale: 0.96 }}
                                                            transition={{ duration: 0.22, ease: "easeOut" }}
                                                            onMouseEnter={() => { if (ourWorkTimer.current) clearTimeout(ourWorkTimer.current); setOurWorkOpen(true); }}
                                                            onMouseLeave={() => { ourWorkTimer.current = setTimeout(() => setOurWorkOpen(false), 120); }}
                                                            className="absolute top-[calc(100%+12px)] left-1/2 -translate-x-1/2 w-[300px] z-50"
                                                        >
                                                            <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-[0_32px_80px_rgba(0,0,0,0.8)]"
                                                                style={{ background: 'linear-gradient(135deg,#0d0f1a 0%,#0a0c18 100%)' }}>
                                                                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/60 to-transparent" />
                                                                <div className="p-3">
                                                                    <p className="text-[9px] font-bold uppercase tracking-[0.3em] text-blue-400 mb-3 px-2">Our Work</p>
                                                                    {dropdownOurWork.map((item, i) => (
                                                                        <Link
                                                                            key={i}
                                                                            href={item.href}
                                                                            onClick={() => setOurWorkOpen(false)}
                                                                            className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/5 transition-all duration-200 group/ow"
                                                                        >
                                                                            <span className="text-sm font-semibold text-white/70 group-hover/ow:text-white transition-colors leading-snug">{item.label}</span>
                                                                            <svg className="w-3.5 h-3.5 text-white/20 group-hover/ow:text-blue-400 transition-colors shrink-0 ml-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                                                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                                                            </svg>
                                                                        </Link>
                                                                    ))}
                                                                </div>
                                                                <div className="border-t border-white/5 px-5 py-2.5 flex justify-end">
                                                                    <Link href="/our-work" onClick={() => setOurWorkOpen(false)} className="text-[10px] font-bold tracking-[0.15em] uppercase text-white/40 hover:text-white transition-colors">
                                                                        View all →
                                                                    </Link>
                                                                </div>
                                                            </div>
                                                        </motion.div>
                                                    </>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                    ) : item.hasDropdown ? (
                                        <div
                                            key={item.name}
                                            ref={dropdownRef}
                                            className="relative"
                                            onMouseEnter={openDropdown}
                                            onMouseLeave={closeDropdown}
                                        >
                                            <Link
                                                href={item.href}
                                                className={`text-[11px] uppercase font-bold tracking-[0.08em] transition-all px-4 py-2.5 rounded-full relative flex items-center gap-1.5 whitespace-nowrap ${pathname.startsWith('/services')
                                                    ? 'text-white'
                                                    : 'text-white/50 hover:text-white'
                                                    }`}
                                            >
                                                {pathname.startsWith('/services') && (
                                                    <motion.div
                                                        layoutId="pill-background-futuristic"
                                                        className="absolute inset-0 bg-blue-600 rounded-full"
                                                        initial={false}
                                                        transition={{ type: "spring", stiffness: 500, damping: 35 }}
                                                    >
                                                        <motion.div
                                                            animate={{ x: ['-100%', '100%'] }}
                                                            transition={{ repeat: Infinity, duration: 2, ease: "linear" }}
                                                            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent w-1/2 skew-x-12"
                                                        />
                                                    </motion.div>
                                                )}
                                                <span className="relative z-10">{item.name}</span>
                                                <motion.svg
                                                    animate={{ rotate: servicesOpen ? 180 : 0 }}
                                                    transition={{ duration: 0.2 }}
                                                    className="relative z-10 w-3 h-3 opacity-60"
                                                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                                </motion.svg>
                                            </Link>

                                            {/* Arrow — pinned exactly under Services button */}
                                            <AnimatePresence>
                                                {servicesOpen && (
                                                    <motion.div
                                                        initial={{ opacity: 0 }}
                                                        animate={{ opacity: 1 }}
                                                        exit={{ opacity: 0 }}
                                                        className="absolute top-[calc(100%+4px)] left-1/2 -translate-x-1/2 z-[51]"
                                                        style={{
                                                            width: 0,
                                                            height: 0,
                                                            borderLeft: '8px solid transparent',
                                                            borderRight: '8px solid transparent',
                                                            borderBottom: '8px solid #0d0f1a',
                                                        }}
                                                    />
                                                )}
                                            </AnimatePresence>

                                            {/* Mega Dropdown */}
                                            <AnimatePresence>
                                                {servicesOpen && (
                                                    <motion.div
                                                        initial={{ opacity: 0, y: 10, scale: 0.96 }}
                                                        animate={{ opacity: 1, y: 0, scale: 1 }}
                                                        exit={{ opacity: 0, y: 10, scale: 0.96 }}
                                                        transition={{ duration: 0.22, ease: "easeOut" }}
                                                        onMouseEnter={openDropdown}
                                                        onMouseLeave={closeDropdown}
                                                        className="absolute top-[calc(100%+12px)] left-1/2 -translate-x-1/2 w-[700px] z-50"
                                                    >

                                                        <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-[0_32px_80px_rgba(0,0,0,0.8)] mt-0"
                                                            style={{ background: 'linear-gradient(135deg,#0d0f1a 0%,#0a0c18 100%)' }}>

                                                            {/* Top accent */}
                                                            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/60 to-transparent" />

                                                            <div className="flex">
                                                                {/* Left — service list */}
                                                                <div className="flex-1 p-5">
                                                                    <p className="text-[9px] font-bold uppercase tracking-[0.3em] text-blue-400 mb-4 px-2">What We Do</p>
                                                                    <div className="flex flex-col">
                                                                        {dropdownServices.map((svc, i) => (
                                                                            <Link
                                                                                key={i}
                                                                                href={svc.href}
                                                                                onClick={() => setServicesOpen(false)}
                                                                                onMouseEnter={() => setHoveredSvc(i)}
                                                                                className="group/svc relative flex items-center gap-4 px-3 py-3 rounded-xl transition-all duration-200 hover:bg-white/5 cursor-pointer"
                                                                            >
                                                                                {/* Number */}
                                                                                <span className="text-[10px] font-black font-mono text-white/20 group-hover/svc:text-blue-400 transition-colors w-5 shrink-0">
                                                                                    {String(i + 1).padStart(2, '0')}
                                                                                </span>
                                                                                {/* Active indicator */}
                                                                                <motion.div
                                                                                    animate={{ scaleY: hoveredSvc === i ? 1 : 0, opacity: hoveredSvc === i ? 1 : 0 }}
                                                                                    className="absolute left-0 top-2 bottom-2 w-0.5 rounded-full bg-blue-500 origin-center"
                                                                                />
                                                                                <div className="flex-1 min-w-0">
                                                                                    <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-white/30 group-hover/svc:text-blue-400 transition-colors">{svc.tag}</p>
                                                                                    <p className="text-sm font-bold text-white/70 group-hover/svc:text-white transition-colors leading-snug mt-0.5">{svc.title}</p>
                                                                                </div>
                                                                                <svg className="w-3.5 h-3.5 text-white/20 group-hover/svc:text-blue-400 transition-colors shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                                                                </svg>
                                                                            </Link>
                                                                        ))}
                                                                    </div>
                                                                </div>

                                                                {/* Right — live preview */}
                                                                <div className="w-[220px] shrink-0 relative overflow-hidden rounded-r-2xl">
                                                                    <AnimatePresence mode="wait">
                                                                        <motion.img
                                                                            key={hoveredSvc}
                                                                            src={dropdownServices[hoveredSvc].image}
                                                                            alt={dropdownServices[hoveredSvc].title}
                                                                            initial={{ opacity: 0, scale: 1.06 }}
                                                                            animate={{ opacity: 1, scale: 1 }}
                                                                            exit={{ opacity: 0, scale: 0.98 }}
                                                                            transition={{ duration: 0.3 }}
                                                                            className="absolute inset-0 w-full h-full object-cover"
                                                                            style={{ objectPosition: dropdownServices[hoveredSvc].objectPosition }}
                                                                        />
                                                                    </AnimatePresence>
                                                                    {/* Overlay */}
                                                                    <div className="absolute inset-0 bg-gradient-to-r from-[#0d0f1a] via-[#0d0f1a]/30 to-transparent" />
                                                                    <div className="absolute inset-0 bg-gradient-to-t from-[#0d0f1a]/80 via-transparent to-transparent" />
                                                                    {/* Label */}
                                                                    <div className="absolute bottom-4 left-4 right-4">
                                                                        <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-blue-400">{dropdownServices[hoveredSvc].tag}</p>
                                                                        <p className="text-xs font-bold text-white mt-0.5 leading-snug">{dropdownServices[hoveredSvc].title}</p>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Bottom bar */}
                                                            <div className="border-t border-white/5 px-6 py-3 flex items-center justify-between">
                                                                <span className="text-[9px] text-white/20 tracking-[0.2em] uppercase">6 core services</span>
                                                                <Link
                                                                    href="/services"
                                                                    onClick={() => setServicesOpen(false)}
                                                                    className="flex items-center gap-1.5 text-[10px] font-bold tracking-[0.15em] uppercase text-white/40 hover:text-white transition-colors"
                                                                >
                                                                    View all services →
                                                                </Link>
                                                            </div>
                                                        </div>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                    ) : (
                                        <Link
                                            key={item.name}
                                            href={item.href}
                                            className={`text-[11px] uppercase font-bold tracking-[0.08em] transition-all px-4 py-2.5 rounded-full relative group/item whitespace-nowrap ${pathname === item.href
                                                ? 'text-white'
                                                : 'text-white/50 hover:text-white'
                                                }`}
                                        >
                                            {pathname === item.href && (
                                                <motion.div
                                                    layoutId="pill-background-futuristic"
                                                    className="absolute inset-0 bg-blue-600 rounded-full"
                                                    initial={false}
                                                    transition={{ type: "spring", stiffness: 500, damping: 35 }}
                                                >
                                                    <motion.div
                                                        animate={{ x: ['-100%', '100%'] }}
                                                        transition={{ repeat: Infinity, duration: 2, ease: "linear" }}
                                                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent w-1/2 skew-x-12"
                                                    />
                                                </motion.div>
                                            )}
                                            <span className="relative z-10">{item.name}</span>
                                        </Link>
                                    )
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Desktop Contact Button */}
                    <div className="hidden lg:flex items-center gap-3">
                        <a
                            href={PHONE_TEL}
                            aria-label={`Call Lead Workz on ${PHONE_DISPLAY}`}
                            className="flex items-center gap-2 px-3 py-2.5 rounded-full bg-zinc-900/80 border border-white/10 text-white hover:text-[#CEFF1A] transition-colors whitespace-nowrap"
                        >
                            <Phone className="w-3.5 h-3.5" />
                            <span className="hidden xl:inline text-[11px] font-bold tracking-[0.06em]">{PHONE_DISPLAY}</span>
                        </a>
                        <Link
                            href="/contact"
                            className="relative group/contact flex items-center gap-2 px-5 py-2.5 rounded-full overflow-hidden whitespace-nowrap"
                            style={{
                                background: 'linear-gradient(135deg, #1d4ed8 0%, #3730a3 100%)',
                                boxShadow: '0 0 18px rgba(99, 102, 241, 0.45), inset 0 1px 0 rgba(255,255,255,0.15)',
                            }}
                        >
                            {/* Shimmer sweep */}
                            <motion.div
                                animate={{ x: ['-120%', '220%'] }}
                                transition={{ repeat: Infinity, duration: 2.8, ease: 'linear', repeatDelay: 1.2 }}
                                className="absolute inset-0 w-1/3 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12 pointer-events-none"
                            />
                            {/* Pulse dot */}
                            <span className="relative z-10 flex items-center justify-center w-1.5 h-1.5">
                                <span className="absolute inline-flex w-full h-full rounded-full opacity-60 animate-ping" style={{ backgroundColor: '#CEFF1A' }} />
                                <span className="relative inline-flex w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#CEFF1A' }} />
                            </span>
                            <span className="relative z-10 text-[11px] uppercase font-bold tracking-[0.1em] text-white">
                                Contact
                            </span>
                            <svg
                                className="relative z-10 w-3 h-3 text-white/60 group-hover/contact:translate-x-0.5 transition-transform duration-200"
                                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                            </svg>
                        </Link>
                    </div>

                    {/* Mobile Menu Toggle */}
                    <div className="lg:hidden flex items-center gap-3">
                        <a
                            href={PHONE_TEL}
                            aria-label={`Call Lead Workz on ${PHONE_DISPLAY}`}
                            className="p-3 text-[#CEFF1A] bg-zinc-900/90 backdrop-blur-lg rounded-2xl border border-white/10 shadow-2xl flex items-center justify-center transition-all active:scale-95"
                        >
                            <Phone className="w-5 h-5" />
                        </a>
                        <button
                            className="p-3 text-white bg-zinc-900/90 backdrop-blur-lg rounded-2xl border border-white/10 shadow-2xl flex items-center justify-center transition-all active:scale-95"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                            aria-expanded={mobileMenuOpen}
                            aria-controls="mobile-menu"
                        >
                            <div className="w-6 h-5 relative flex flex-col justify-between">
                                <span className={`w-full h-0.5 bg-white transition-all ${mobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`} />
                                <span className={`w-full h-0.5 bg-white transition-all ${mobileMenuOpen ? 'opacity-0' : ''}`} />
                                <span className={`w-full h-0.5 bg-white transition-all ${mobileMenuOpen ? '-rotate-45 -translate-y-2.5' : ''}`} />
                            </div>
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Menu */}
            <AnimatePresence>
                {mobileMenuOpen && (
                    <motion.div
                        id="mobile-menu"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="lg:hidden overflow-hidden bg-zinc-950/95 backdrop-blur-xl border-t border-white/5 mt-4"
                    >
                        <div className="flex flex-col p-6 gap-1">
                            {navItems.map((item) =>
                                item.hasOurWorkDropdown ? (
                                    <div key={item.name}>
                                        <div className={`w-full flex items-center justify-between text-lg font-bebas tracking-widest ${pathname.startsWith('/our-work') ? 'text-blue-500' : 'text-white'}`}>
                                            <Link
                                                href={item.href}
                                                onClick={() => { setMobileMenuOpen(false); setOurWorkOpen(false); }}
                                                className="flex-1 py-3"
                                            >
                                                {item.name}
                                            </Link>
                                            <button
                                                className="p-3 -mr-3"
                                                onClick={() => setOurWorkOpen(!ourWorkOpen)}
                                                aria-label="Toggle Our Work menu"
                                                aria-expanded={ourWorkOpen}
                                            >
                                                <motion.svg
                                                    animate={{ rotate: ourWorkOpen ? 180 : 0 }}
                                                    transition={{ duration: 0.2 }}
                                                    className="w-4 h-4 opacity-60"
                                                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                                </motion.svg>
                                            </button>
                                        </div>
                                        <AnimatePresence>
                                            {ourWorkOpen && (
                                                <motion.div
                                                    initial={{ opacity: 0, height: 0 }}
                                                    animate={{ opacity: 1, height: 'auto' }}
                                                    exit={{ opacity: 0, height: 0 }}
                                                    className="overflow-hidden"
                                                >
                                                    <div className="flex flex-col gap-1 pl-3 pb-2 border-l border-white/10 ml-1 mt-1">
                                                        <Link
                                                            href={item.href}
                                                            onClick={() => { setMobileMenuOpen(false); setOurWorkOpen(false); }}
                                                            className="py-2.5 px-3 rounded-xl hover:bg-white/5 transition-colors text-sm font-semibold text-white"
                                                        >
                                                            All Our Work
                                                        </Link>
                                                        {dropdownOurWork.map((ow, i) => (
                                                            <Link
                                                                key={i}
                                                                href={ow.href}
                                                                onClick={() => { setMobileMenuOpen(false); setOurWorkOpen(false); }}
                                                                className="py-2.5 px-3 rounded-xl hover:bg-white/5 transition-colors text-sm font-semibold text-white/80"
                                                            >
                                                                {ow.label}
                                                            </Link>
                                                        ))}
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                ) : item.hasDropdown ? (
                                    <div key={item.name}>
                                        {/* Services row — tap to toggle sub-menu */}
                                        <button
                                            className={`w-full flex items-center justify-between py-3 text-lg font-bebas tracking-widest ${pathname.startsWith('/services') ? 'text-blue-500' : 'text-white'}`}
                                            onClick={() => setServicesOpen(!servicesOpen)}
                                            aria-label="Toggle Services menu"
                                            aria-expanded={servicesOpen}
                                        >
                                            <span>{item.name}</span>
                                            <motion.svg
                                                animate={{ rotate: servicesOpen ? 180 : 0 }}
                                                transition={{ duration: 0.2 }}
                                                className="w-4 h-4 opacity-60"
                                                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                            </motion.svg>
                                        </button>

                                        {/* Services sub-menu */}
                                        <AnimatePresence>
                                            {servicesOpen && (
                                                <motion.div
                                                    initial={{ opacity: 0, height: 0 }}
                                                    animate={{ opacity: 1, height: 'auto' }}
                                                    exit={{ opacity: 0, height: 0 }}
                                                    className="overflow-hidden"
                                                >
                                                    <div className="flex flex-col gap-1 pl-3 pb-2 border-l border-white/10 ml-1 mt-1">
                                                        {dropdownServices.map((svc, i) => (
                                                            <Link
                                                                key={i}
                                                                href={svc.href}
                                                                onClick={() => { setMobileMenuOpen(false); setServicesOpen(false); }}
                                                                className="flex flex-col py-2.5 px-3 rounded-xl hover:bg-white/5 transition-colors"
                                                            >
                                                                <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-blue-400">{svc.tag}</span>
                                                                <span className="text-sm font-semibold text-white/80 mt-0.5 leading-snug">{svc.title}</span>
                                                            </Link>
                                                        ))}
                                                        <Link
                                                            href="/services"
                                                            onClick={() => { setMobileMenuOpen(false); setServicesOpen(false); }}
                                                            className="text-xs text-white/40 px-3 py-2 hover:text-white transition-colors"
                                                        >
                                                            View all services →
                                                        </Link>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                ) : (
                                    <Link
                                        key={item.name}
                                        href={item.href}
                                        onClick={() => setMobileMenuOpen(false)}
                                        className={`py-3 text-lg font-bebas tracking-widest ${pathname === item.href ? 'text-blue-500' : 'text-white'}`}
                                    >
                                        {item.name}
                                    </Link>
                                )
                            )}
                            <Link
                                href="/contact"
                                onClick={() => setMobileMenuOpen(false)}
                                className={`py-3 text-lg font-bebas tracking-widest ${pathname === '/contact' ? 'text-blue-500' : 'text-white'}`}
                            >
                                Contact
                            </Link>
                            <button
                                onClick={() => { setMobileMenuOpen(false); openModal(); }}
                                className="bg-gradient-to-r from-blue-600 to-blue-400 text-white text-center py-4 rounded-xl font-bold mt-3"
                            >
                                Start a Project
                            </button>
                            <div className="grid grid-cols-2 gap-3 mt-3">
                                <a
                                    href={PHONE_TEL}
                                    className="flex items-center justify-center gap-2 py-3.5 rounded-xl border border-white/15 text-white font-bold"
                                >
                                    <Phone className="w-4 h-4" />
                                    Call Us
                                </a>
                                <a
                                    href={whatsappHref(DEFAULT_WHATSAPP_MESSAGE)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-center gap-2 py-3.5 rounded-xl bg-[#25D366] text-white font-bold"
                                >
                                    WhatsApp
                                </a>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.nav>
    );
}
